import React from "react";

import { useTranslation } from "react-i18next";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";

import * as Sentry from "@sentry/react";

import { ApiError } from "@/api/client";
import { Button, buttonVariants } from "@/components/ui/button";

export default function RootErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);

  const status = isRouteErrorResponse(error)
    ? error.status
    : error instanceof ApiError
      ? error.status
      : undefined;
  const is404 = status === 404;
  const uiTitle = is404 ? t("common.error.404.title") : t("common.error.title");
  const uiMessage = is404
    ? t("common.error.404.message")
    : t("common.error.message");

  if (!is404) {
    Sentry.captureException(error);
  }

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : JSON.stringify(error);
  const stack = error instanceof Error ? error.stack : null;

  return (
    <main className="bg-background grid min-h-full place-items-center px-6 py-24 sm:py-32 lg:px-8">
      <div className="flex flex-col items-center text-center">
        <h1 className="text-foreground mt-4 text-3xl font-bold tracking-tight sm:text-5xl">
          {uiTitle}
        </h1>
        <p className="text-muted-foreground mt-6 max-w-xl text-center leading-7">
          {uiMessage}
        </p>
        {!is404 && (
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <a href="/" className={buttonVariants()}>
              {t("common.error.reload")}
            </a>
            <Button variant="link" onClick={() => setShowDetails(true)}>
              {t("common.error.showDetails")}{" "}
              <span aria-hidden="true">&rarr;</span>
            </Button>
          </div>
        )}
      </div>

      {showDetails && (
        <div className="w-full max-w-4xl">
          <div className="relative my-8 w-full">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="border-border w-full border-t" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background text-muted-foreground px-2 text-sm">
                {t("common.error.details")}
              </span>
            </div>
          </div>

          <h2 className="text-muted-foreground mb-2 ml-6 text-left text-xl font-bold tracking-tight sm:text-2xl">
            {message}
          </h2>
          <div className="bg-muted overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              {stack ? (
                <pre className="overflow-x-auto text-xs">{stack}</pre>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
